<template>
  <section v-if="enabled" class="dc-entry" data-testid="damage-control-entry" :aria-busy="loading || busy ? 'true' : 'false'">
    <h2 class="text-subtitle-1 font-weight-bold">{{ $t('damage_control.entry.title') }}</h2>
    <p v-if="loading" role="status">{{ $t('damage_control.strict.checking') }}</p>
    <p v-else-if="!loadFailed" role="status" data-testid="inspection-status">{{ statusText }}</p>
    <v-alert v-if="loadFailed" type="warning" outlined data-testid="inspection-load-error">
      {{ $t(strictEnabled ? 'damage_control.entry.check_failed' : 'damage_control.entry.load_failed') }}
      <v-btn text class="dc-entry-action" data-testid="inspection-retry" :disabled="busy" @click="loadState">{{ $t('damage_control.entry.retry') }}</v-btn>
    </v-alert>
    <v-alert v-if="errorText" type="warning" outlined data-testid="inspection-action-error">
      {{ errorText }}
      <p v-if="blockingInspectionId" class="mt-2 mb-0">{{ $t('damage_control.entry.blocking_id', { id: blockingInspectionId }) }}</p>
      <v-btn v-if="blockingInspectionId && isManagerPlus" text class="dc-entry-action" :disabled="busy || loading"
             data-testid="blocking-inspection-close" @click="requestClose(blockingInspectionId, true)">{{ $t('damage_control.entry.close_incomplete') }}</v-btn>
    </v-alert>
    <div class="dc-entry-actions">
      <v-btn block v-if="!completed && isStaff" color="primary" class="dc-entry-action" :loading="busy" :disabled="loading || busy"
             data-testid="inspection-start" @click="start">{{ $t(inProgress ? 'damage_control.entry.resume' : 'damage_control.entry.start') }}</v-btn>
      <v-btn block v-if="completed" color="primary" class="dc-entry-action" data-testid="inspection-open-act"
             :to="{ name: 'damage-control-act', params: { booking_id: booking.id } }">{{ $t('damage_control.entry.open_act') }}</v-btn>
      <v-btn block v-if="completed && isManagerPlus" outlined class="dc-entry-action" :disabled="loading || busy"
             data-testid="inspection-reshoot" @click="reshoot">{{ $t('damage_control.entry.reshoot') }}</v-btn>
      <v-btn block v-if="inProgress && isManagerPlus" outlined class="dc-entry-action" :disabled="loading || busy"
             data-testid="inspection-close" @click="requestClose(currentInspection.id)">{{ $t('damage_control.entry.close_incomplete') }}</v-btn>
    </div>
    <v-dialog v-if="closeTarget && isManagerPlus" :value="true" max-width="480" persistent>
      <v-card>
        <v-card-text class="pt-5">{{ $t('damage_control.entry.close_confirm') }}</v-card-text>
        <v-card-actions class="dc-entry-actions pa-4">
          <v-btn text class="dc-entry-action" :disabled="busy" @click="closeTarget = null">{{ $t('damage_control.entry.cancel') }}</v-btn>
          <v-btn color="warning" class="dc-entry-action" :loading="busy" :disabled="busy" data-testid="inspection-confirm-close" @click="confirmClose">{{ $t('damage_control.entry.confirm_close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <inspection-screen v-if="screenInspection" :key="screenInspection.id" :inspection="screenInspection"
                       @update:inspection="updateInspection" @completed="finishScreen" @close="finishScreen" />
  </section>
</template>

<script>
import { damageControlApi, damageControlStrict, parseDamageControlError, reportDamageControlFailure } from "@/api/damageControl";
import { acceptedCurrentShot } from "@/utils/damageControlReview";
import { strictBlock } from "@/utils/damageControlStrict";
import roleMixin from "@/mixins/roleMixin";
import damageControlAccessMixin from "@/mixins/damageControlAccessMixin";
import InspectionScreen from "../capture/InspectionScreen.vue";

export default {
  name: "DamageControlEntry",
  components: { InspectionScreen },
  mixins: [roleMixin, damageControlAccessMixin],
  props: {
    booking: { type: Object, required: true },
    side: { type: String, required: true, validator: value => ["departure", "arrival"].includes(value) },
  },
  data() {
    return { inspections: [], inspection: null, screenInspection: null, loading: true, busy: false,
      loadFailed: false, errorText: "", blockingInspectionId: null, closeTarget: null,
      generation: 0, loadSequence: 0, disposed: false, lastBlockingSignature: null };
  },
  computed: {
    enabled() { return this.damageControlAvailable; },
    strictEnabled() { return damageControlStrict(this.damageControlCompany); },
    contextKey() { return JSON.stringify([this.damageControlCompany && this.damageControlCompany.id, this.booking.id, this.booking.car_id, this.side, this.enabled]); },
    currentInspection() {
      return this.inspections.filter(item => item.car_id === this.booking.car_id && item.side === this.side && ["in_progress", "completed"].includes(item.status))
        .sort((a, b) => b.version - a.version || b.id - a.id)[0] || null;
    },
    completed() { return this.currentInspection && this.currentInspection.status === "completed"; },
    inProgress() { return this.currentInspection && this.currentInspection.status === "in_progress"; },
    requiredZones() { return this.inspection ? (this.inspection.zones || []).filter(zone => zone.required) : []; },
    requiredCount() { return this.requiredZones.length; },
    capturedCount() { return this.requiredZones.filter(acceptedCurrentShot).length; },
    statusText() {
      if (this.completed) return this.$t("damage_control.entry.status_completed");
      if (this.inProgress) return this.$t("damage_control.entry.status_started", { captured: this.capturedCount, total: this.requiredCount });
      return this.$t("damage_control.entry.status_none");
    },
    blockingState() {
      if (!this.enabled) return { blocked: false, reasons: [] };
      if (!damageControlStrict(this.damageControlCompany)) return { blocked: false, reasons: [] };
      if (this.loadFailed) return { blocked: true, reasons: [{ reason: "damage_control.entry.check_failed" }] };
      if (this.loading) return { blocked: true, reasons: [{ reason: "damage_control.strict.checking" }] };
      return strictBlock({ company: this.damageControlCompany, booking: this.booking, side: this.side, inspections: this.inspections, inspection: this.inspection });
    },
  },
  watch: {
    contextKey: { immediate: true, handler() { this.resetContext(); } },
    blockingState: { immediate: true, deep: true, handler(value) {
      const signature = JSON.stringify([value.blocked, value.reasons]);
      if (signature === this.lastBlockingSignature) return;
      this.lastBlockingSignature = signature;
      this.$emit("blocking", value.blocked, value.reasons);
    } },
  },
  beforeDestroy() { this.disposed = true; this.generation += 1; this.loadSequence += 1; },
  methods: {
    resetContext() {
      this.generation += 1;
      this.loadSequence += 1;
      this.inspections = [];
      this.inspection = null;
      this.screenInspection = null;
      this.closeTarget = null;
      this.blockingInspectionId = null;
      this.errorText = "";
      this.busy = false;
      this.loadFailed = false;
      this.loading = this.enabled;
      if (this.enabled) this.loadState();
    },
    active(generation, context) { return !this.disposed && this.enabled && generation === this.generation && context === this.contextKey; },
    async loadState() {
      if (!this.enabled || this.disposed) return;
      const generation = this.generation;
      const context = this.contextKey;
      const sequence = ++this.loadSequence;
      const active = () => this.active(generation, context) && sequence === this.loadSequence;
      this.loading = true;
      this.loadFailed = false;
      try {
        const act = await damageControlApi.getAct(this.axios, this.booking.id);
        if (!active()) return;
        this.inspections = act.inspections || [];
        this.inspection = null;
        const current = this.currentInspection;
        if (current && current.status === "in_progress") {
          const detail = await damageControlApi.getInspection(this.axios, current.id);
          if (!active()) return;
          this.updateInspection(detail);
        }
      } catch (error) {
        if (!active()) return;
        this.loadFailed = true;
        reportDamageControlFailure(this, error, { silent: true });
      } finally { if (active()) this.loading = false; }
    },
    updateInspection(payload) {
      if (!this.enabled || !payload || payload.car_id !== this.booking.car_id || payload.side !== this.side || payload.booking_id !== this.booking.id) return;
      this.inspection = payload;
      this.inspections = this.inspections.filter(item => item.id !== payload.id).concat(payload);
      if (this.screenInspection) this.screenInspection = payload;
    },
    actionFailure(error, reshoot = false) {
      const parsed = parseDamageControlError(error);
      const details = parsed ? { ...parsed.details, ...(parsed.details.details || {}) } : {};
      this.blockingInspectionId = null;
      // The current inspections controller omits result.message. Reshoot checks
      // later inspections before the unfinished guard; never offer to close that ID.
      const later = reshoot && parsed && parsed.code === "conflict" && details.blocking_inspection_id && !details.message;
      if (later || details.message === "Car has a later inspection") this.errorText = this.$t("damage_control.entry.later_inspection");
      else if (parsed && parsed.status === 409 && details.blocking_inspection_id) {
        this.blockingInspectionId = details.blocking_inspection_id;
        this.errorText = this.$t("damage_control.entry.blocking_inspection");
      } else if (details.message === "Booking has no car") this.errorText = this.$t("damage_control.entry.no_car");
      else this.errorText = parsed ? this.$t(parsed.i18nKey) : reportDamageControlFailure(this, error, { silent: true });
    },
    async openInspection(reshoot = false) {
      if (!this.enabled || !this.isStaff || this.busy || this.loading || (reshoot && (!this.isManagerPlus || !this.completed))) return;
      this.blockingInspectionId = null;
      if (this.booking.car_id == null) {
        this.errorText = this.$t("damage_control.entry.no_car");
        return;
      }
      const generation = this.generation;
      const context = this.contextKey;
      this.busy = true;
      this.errorText = "";
      try {
        const payload = reshoot
          ? await damageControlApi.reshootInspection(this.axios, this.currentInspection.id)
          : await damageControlApi.startInspection(this.axios, this.booking.id, this.side);
        if (!this.active(generation, context)) return;
        // Reshoot archives the previous completion atomically on the server.
        if (reshoot) this.inspections = this.inspections.filter(item => item.car_id !== this.booking.car_id || item.side !== this.side);
        this.updateInspection(payload);
        this.loadFailed = false;
        this.screenInspection = this.inspection;
      } catch (error) { if (this.active(generation, context)) this.actionFailure(error, reshoot); }
      finally { if (this.active(generation, context)) this.busy = false; }
    },
    start() { return this.openInspection(); },
    reshoot() { return this.openInspection(true); },
    requestClose(id, restart = false) {
      if (!this.enabled || !this.isManagerPlus || this.busy || this.loading) return;
      this.closeTarget = { id, restart };
    },
    async confirmClose() {
      if (!this.enabled || !this.isManagerPlus || !this.closeTarget || this.busy) return;
      const generation = this.generation;
      const context = this.contextKey;
      const target = this.closeTarget;
      this.busy = true;
      this.errorText = "";
      let closed = false;
      try {
        await damageControlApi.closeInspection(this.axios, target.id);
        if (!this.active(generation, context)) return;
        this.closeTarget = null;
        this.blockingInspectionId = null;
        closed = true;
      } catch (error) { if (this.active(generation, context)) this.actionFailure(error); }
      finally { if (this.active(generation, context)) this.busy = false; }
      if (!closed || !this.active(generation, context)) return;
      if (target.restart) await this.start();
      else await this.loadState();
    },
    finishScreen() {
      this.screenInspection = null;
      return this.loadState();
    },
  },
};
</script>

<style scoped>
.dc-entry { min-width: 0; padding: 16px; border: 1px solid #8886; border-radius: 8px; overflow-wrap: anywhere; }
.dc-entry-actions { display: flex; flex-wrap: wrap; gap: 8px; }
.dc-entry-action.v-btn { max-width: 100%; height: auto; min-height: 36px; padding-top: 8px; padding-bottom: 8px; white-space: normal; }
.dc-entry-action ::v-deep .v-btn__content { white-space: normal; flex: 1 1 auto; }
.dc-entry-actions > .v-btn { margin: 0; }
</style>
